/**
 * HR "Change requests" tab — /hr/coverage?tab=requests (capacityChangeRequests).
 *
 * HR records an offline-approved team capacity change with the requesting
 * manager, justification, effectivity (permanent or a date range), and
 * supporting documents. Recording applies the new cap to the team and appends
 * to the browsable history below. Backed by /api/v1/hr/capacity-requests.
 */
import { useState } from 'react'
import type { CSSProperties } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Employee } from '@lms/shared-types'

import { listTeams } from '../../api/teams'
import {
  createCapacityRequest,
  uploadCapacityRequestDocument,
} from '../../api/capacity-requests'
import { Button } from '../../components/primitives/Button'
import { Card } from '../../components/primitives/Card'
import { DateField } from '../../components/primitives/DateField'
import { EmployeePickerInput } from '../../components/primitives/EmployeePickerInput'
import { FileDropzone } from '../../components/primitives/FileDropzone'
import { useToast } from '../../components/primitives/Toast'
import { changeLabel, errMsg } from './capacity-format'

const fieldStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }
const labelStyle: CSSProperties = { fontSize: 13, fontWeight: 600, color: 'var(--text)' }
const hintStyle: CSSProperties = { fontSize: 12, color: 'var(--text-3)' }
const controlStyle: CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: 13,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  minHeight: 44,
  boxSizing: 'border-box',
}

function toIntOrNull(s: string): number | null {
  const t = s.trim()
  if (t === '') return null
  const n = Number.parseInt(t, 10)
  return Number.isNaN(n) ? null : n
}

export function CapacityRequestsTab(): JSX.Element {
  const { notify } = useToast()
  const queryClient = useQueryClient()

  const [teamId, setTeamId] = useState<string>('')
  const [requestedBy, setRequestedBy] = useState<Employee | null>(null)
  const [newSlotPct, setNewSlotPct] = useState<string>('')
  const [newFloor, setNewFloor] = useState<string>('')
  const [mode, setMode] = useState<'permanent' | 'range'>('permanent')
  const [effectiveFrom, setEffectiveFrom] = useState<string | null>(null)
  const [effectiveTo, setEffectiveTo] = useState<string | null>(null)
  const [reason, setReason] = useState<string>('')
  const [files, setFiles] = useState<File[]>([])

  const teamsQuery = useQuery({ queryKey: ['teams'], queryFn: listTeams, staleTime: 30_000 })

  const hasChange = toIntOrNull(newSlotPct) != null || toIntOrNull(newFloor) != null
  const rangeOk = mode === 'permanent' || (effectiveFrom != null && effectiveTo != null)
  const canSubmit = teamId !== '' && reason.trim().length > 0 && hasChange && rangeOk

  function resetForm(): void {
    setTeamId('')
    setRequestedBy(null)
    setNewSlotPct('')
    setNewFloor('')
    setMode('permanent')
    setEffectiveFrom(null)
    setEffectiveTo(null)
    setReason('')
    setFiles([])
  }

  const createMutation = useMutation({
    mutationFn: async () => {
      const record = await createCapacityRequest({
        teamId,
        requestedById: requestedBy ? requestedBy.id : null,
        reason: reason.trim(),
        newSlotPct: toIntOrNull(newSlotPct),
        newFloor: toIntOrNull(newFloor),
        effectiveFrom: mode === 'range' ? effectiveFrom : null,
        effectiveTo: mode === 'range' ? effectiveTo : null,
      })
      for (const f of files) {
        await uploadCapacityRequestDocument(record.id, f)
      }
      return record
    },
    onSuccess: (record) => {
      notify(`Recorded · ${record.teamName} ${changeLabel(record)}`, 'success')
      resetForm()
      void queryClient.invalidateQueries({ queryKey: ['capacity-requests'] })
      void queryClient.invalidateQueries({ queryKey: ['teams'] })
    },
    onError: (e) => notify(`Save failed — ${errMsg(e)}`, 'error'),
  })

  const teams = teamsQuery.data ?? []

  return (
    <div style={{ maxWidth: 720 }} data-testid="capacity-requests">
      <p style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 20, lineHeight: 1.5 }}>
        Record a capacity change that was requested and approved out of system.
        A permanent change updates the team's baseline cap; a date range is a
        live window the leave gate honours only on those dates. Kept here with
        the requesting manager, justification, effectivity, and documents.
      </p>

      <Card padding={20}>
        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="cap-team">
            Team
          </label>
          <select
            id="cap-team"
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
            style={controlStyle}
            data-testid="cap-team"
          >
            <option value="">Select a team…</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} — cap {t.slotPct}%{t.floor != null ? `, floor ${t.floor}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Requested by (manager)</label>
          <EmployeePickerInput
            value={requestedBy}
            onChange={setRequestedBy}
            testId="cap-requested-by"
            placeholder="Search the requesting manager…"
          />
        </div>

        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{ ...fieldStyle, flex: 1 }}>
            <label style={labelStyle} htmlFor="cap-slot">
              New absence cap %
            </label>
            <input
              id="cap-slot"
              type="number"
              min={0}
              max={100}
              value={newSlotPct}
              onChange={(e) => setNewSlotPct(e.target.value)}
              placeholder="e.g. 45"
              style={controlStyle}
              data-testid="cap-slot"
            />
          </div>
          <div style={{ ...fieldStyle, flex: 1 }}>
            <label style={labelStyle} htmlFor="cap-floor">
              New min-present floor
            </label>
            <input
              id="cap-floor"
              type="number"
              min={0}
              value={newFloor}
              onChange={(e) => setNewFloor(e.target.value)}
              placeholder="optional"
              style={controlStyle}
              data-testid="cap-floor"
            />
          </div>
        </div>
        <span style={{ ...hintStyle, marginTop: -8, marginBottom: 16, display: 'block' }}>
          Set at least one. Leave a field blank to keep it unchanged.
        </span>

        <div style={fieldStyle}>
          <label style={labelStyle}>Effectivity</label>
          <div style={{ display: 'flex', gap: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="radio"
                name="cap-effectivity"
                checked={mode === 'permanent'}
                onChange={() => setMode('permanent')}
                data-testid="cap-permanent"
              />
              Permanent
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="radio"
                name="cap-effectivity"
                checked={mode === 'range'}
                onChange={() => setMode('range')}
                data-testid="cap-range"
              />
              Date range
            </label>
          </div>
          {mode === 'range' ? (
            <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
              <div style={{ flex: 1 }}>
                <DateField value={effectiveFrom} onChange={setEffectiveFrom} data-testid="cap-from" />
              </div>
              <div style={{ flex: 1 }}>
                <DateField value={effectiveTo} onChange={setEffectiveTo} data-testid="cap-to" />
              </div>
            </div>
          ) : null}
          <span style={hintStyle}>
            Permanent updates the team's baseline cap. A date range is a live
            window: the raised cap applies to leave taken on those dates and
            expires automatically when the window ends, no manual revert.
          </span>
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="cap-reason">
            Reason / justification
          </label>
          <textarea
            id="cap-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="e.g. Peak-season backfill; approved by HR on 2026-09-01."
            style={{ ...controlStyle, minHeight: 60, resize: 'vertical' }}
            data-testid="cap-reason"
          />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Supporting documents</label>
          <FileDropzone
            files={files}
            onChange={setFiles}
            testIdPrefix="cap-docs"
            hint="Optional. The offline request / approval evidence, attached to this record."
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="primary"
            onClick={() => createMutation.mutate()}
            disabled={!canSubmit || createMutation.isPending}
            data-testid="cap-submit"
          >
            {createMutation.isPending ? 'Recording…' : 'Record capacity change'}
          </Button>
        </div>
      </Card>
    </div>
  )
}
